import { Clock, GripVertical, Plus, Trash } from 'lucide-react';
import { AgendaItem } from '@/types/meeting';

interface AgendaBuilderProps {
  agenda: AgendaItem[];
  onChange: (agenda: AgendaItem[]) => void;
}

export function AgendaBuilder({ agenda, onChange }: AgendaBuilderProps) {
  const addItem = () => {
    const newItem: AgendaItem = {
      id: `agenda-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: '',
      duration: 15,
      description: '',
    };
    onChange([...agenda, newItem]);
  };

  const updateItem = (index: number, updates: Partial<AgendaItem>) => {
    const newAgenda = [...agenda];
    newAgenda[index] = { ...newAgenda[index], ...updates };
    onChange(newAgenda);
  };

  const removeItem = (index: number) => {
    onChange(agenda.filter((_, i) => i !== index));
  };

  // const moveItem = (fromIndex: number, toIndex: number) => {
  //   const newAgenda = [...agenda];
  //   const [removed] = newAgenda.splice(fromIndex, 1);
  //   newAgenda.splice(toIndex, 0, removed);
  //   onChange(newAgenda);
  // };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Meeting Agenda</h3>
      <div className="space-y-3">
        {agenda.length === 0 ? (
          <div className="flex items-center justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-900 mb-1">No agenda items</h4>
              <p className="text-sm text-gray-500">Add your first agenda item using the button below</p>
            </div>
          </div>
        ) : agenda.map((item, index) => (
          <div
            key={item.id}
            className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-gray-200"
          >
            <button
              type="button"
              className="mt-2 text-gray-400 hover:text-gray-600"
              aria-label="Reorder item"
            >
              <GripVertical className="w-4 h-4" />
            </button>

            <div className="flex-grow space-y-3">
              <input
                type="text"
                value={item.title}
                onChange={(e) => updateItem(index, { title: e.target.value })}
                placeholder="Agenda item title"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />

              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 text-gray-400 mr-1" />
                  <input
                    type="number"
                    value={item.duration}
                    onChange={(e) => updateItem(index, { duration: parseInt(e.target.value) })}
                    min="1"
                    className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <span className="ml-1 text-sm text-gray-500">min</span>
                </div>
              </div>

              <textarea
                value={item.description}
                onChange={(e) => updateItem(index, { description: e.target.value })}
                placeholder="Item description or notes"
                rows={2}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <button
              type="button"
              onClick={() => removeItem(index)}
              className="text-gray-400 hover:text-red-600"
              aria-label="Remove item"
            >
              <Trash className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      
      <div className="flex justify-center">
        <button
          type="button"
          onClick={addItem}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Agenda Item
        </button>
      </div>
    </div>
  );
}